import React, { memo, useCallback, useMemo, useState } from "react";
import { FlatList } from "react-native";
import BulkRequestItemComponent from "@components/organisms/BulkRequestItemComponent";
import { useTheme } from "styled-components/native";
import EditBulkItemModal from "./EditBulkItemModal";
import PopUp from "@components/molecules/PopUp";
import { useTranslation } from "react-i18next";
import { ButtonType } from "@components/atoms/Button/types";
import { messageType } from "@components/molecules/PopUp/types";
import { SelectedItemsProvider } from "../../../contexts/SelectedItemsContext";
import { StoneType } from "@scenes/bulk-request/Utils";

interface RenderedItemProps {
  item: any;
  i?: any;
  stockItemRequest?: any;
  fromRequestDetail?: boolean;
  fromBulkList?: boolean;
  fromRequestModify?: boolean;
  changeEditCommentModalIndex: (id: number) => void;
  showCheckbox?: boolean;
  checkBoxSelection?: any;
  showListPrice?: boolean;
  isTotalOfferDiscountApplied?: boolean;
  viewType?: any;
  requestType?: string;
  hasAnyOfferDetails?: boolean;
  childValues?: any;
  stoneType?: string;
  _saveBulkItemDetails: (id: number, details: any, fromParcel: boolean) => void;
}

const RenderedItem = memo(
  ({
    item,
    i,
    stockItemRequest,
    fromRequestDetail,
    fromBulkList,
    fromRequestModify,
    changeEditCommentModalIndex,
    showCheckbox,
    checkBoxSelection,
    showListPrice,
    isTotalOfferDiscountApplied,
    viewType,
    requestType,
    hasAnyOfferDetails,
    childValues,
    stoneType,
    _saveBulkItemDetails,
  }: RenderedItemProps) => {
    const matchingItem =
      stockItemRequest &&
      stockItemRequest?.children?.find(
        (child: any) =>
          child?.[item.type ? item.type : item.stock_type].id === item?.id
      );
    const testID = `bulk-init-summary-list.item-${i}`;
    return (
      <BulkRequestItemComponent
        key={i}
        onPress={() => {}}
        {...(fromBulkList && { testID, dataTestId: testID })}
        mItem={
          item.type
            ? item
            : {
                ...item.item,
                quantity: item?.quantity,
                cart_item_id: item?.id,
              }
        }
        productType={item.type ? item.type : item.stock_type}
        fromRequestDetail={fromRequestDetail}
        {...((fromBulkList ||
          (fromRequestModify &&
            !(
              matchingItem?.status === "decline" ||
              matchingItem?.status === "withdraw"
            ))) && {
          changeEditCommentModalIndex: changeEditCommentModalIndex,
        })}
        stockItemRequest={stockItemRequest}
        showCheckbox={showCheckbox}
        checkBoxSelection={checkBoxSelection}
        hideBorder={fromRequestDetail || fromRequestModify}
        fromRequestModify={fromRequestModify}
        showListPrice={showListPrice}
        isTotalOfferDiscountApplied={isTotalOfferDiscountApplied}
        viewType={viewType}
        requestType={requestType}
        hasAnyOfferDetails={hasAnyOfferDetails}
        fromBulkList={fromBulkList}
        childValues={childValues}
        stoneType={stoneType as StoneType}
        saveBulkItemDetails={_saveBulkItemDetails}
      />
    );
  }
);

const BulkInitSummaryList = ({
  itemList,
  removeItemFromList,
  saveBulkItemDetails,
  requestType,
  fromRequestModify,
  fromBulkList,
  fromRequestDetail,
  stockItemRequest,
  backgroundColor,
  showCheckbox = false,
  checkBoxSelection,
  showListPrice,
  isTotalOfferDiscountApplied,
  viewType,
  hasAnyOfferDetails,
  childValues,
  stoneType = StoneType.SINGLE_STONES,
}: any) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [editCommentModalItemIndex, setEditCommentModalItemIndex] = useState<
    number | null
  >(null);
  const [openRemoveFromBulkPopupId, setOpenRemoveFromBulkPopupId] = useState<
    number | null
  >(null);

  const changeEditCommentModalIndex = useCallback(
    (id: number) => {
      let index =
        itemList.findIndex((item: any) =>
          fromRequestModify
            ? item?.id === (id as any)[0]
            : item.stock_item_id === (id as any)[0]
        ) ?? null;
      if (index < 0) index = null;
      setEditCommentModalItemIndex(index);
    },
    [itemList, fromRequestModify]
  );

  const _removeItemFromList = useCallback((id: number) => {
    setOpenRemoveFromBulkPopupId(id);
  }, []);

  const _saveBulkItemDetails = useCallback(
    (id: number, details: any, fromParcel: boolean) => {
      if (fromParcel) {
        if (id && details) {
          saveBulkItemDetails(id, details, fromParcel);
        }
      } else {
        if (id && details) {
          saveBulkItemDetails(id, details, false);
          setEditCommentModalItemIndex(null);
        } else {
          setEditCommentModalItemIndex(null);
        }
      }
    },
    [saveBulkItemDetails]
  );

  const editModalItem = useMemo(
    () =>
      editCommentModalItemIndex !== null
        ? itemList[editCommentModalItemIndex]
        : 0,
    [editCommentModalItemIndex, itemList]
  );

  const editModalProductType = useMemo(
    () =>
      fromRequestModify
        ? itemList[editCommentModalItemIndex ?? 0]?.type
        : itemList[editCommentModalItemIndex ?? 0]?.stock_type ?? "diamond",
    [fromRequestModify, itemList, editCommentModalItemIndex]
  );

  const contentContainerStyle = useMemo(
    () => ({
      backgroundColor: backgroundColor || theme["core-02"],
    }),
    [backgroundColor, theme]
  );

  const renderItem = useCallback(
    ({ item, index }: { item: any; index: number }) => (
      <RenderedItem
        item={item}
        i={index}
        stockItemRequest={stockItemRequest}
        fromRequestDetail={fromRequestDetail}
        fromBulkList={fromBulkList}
        fromRequestModify={fromRequestModify}
        changeEditCommentModalIndex={changeEditCommentModalIndex}
        showCheckbox={showCheckbox}
        checkBoxSelection={checkBoxSelection}
        showListPrice={showListPrice}
        isTotalOfferDiscountApplied={isTotalOfferDiscountApplied}
        viewType={viewType}
        requestType={requestType}
        hasAnyOfferDetails={hasAnyOfferDetails}
        childValues={childValues}
        stoneType={stoneType}
        _saveBulkItemDetails={_saveBulkItemDetails}
      />
    ),
    [
      stockItemRequest,
      fromRequestDetail,
      fromBulkList,
      fromRequestModify,
      changeEditCommentModalIndex,
      showCheckbox,
      checkBoxSelection,
      showListPrice,
      isTotalOfferDiscountApplied,
      viewType,
      requestType,
      hasAnyOfferDetails,
      childValues,
      stoneType,
      _saveBulkItemDetails,
    ]
  );

  const RemoveFromBulkListConfirmPopup = ({ id }: { id: number }) => (
    <PopUp
      title={t("bulkInitScreen.removeFromList.confirmPopup.title.text")}
      buttons={[
        {
          type: ButtonType.primary,
          title: t(
            "bulkInitScreen.removeFromList.confirmPopup.button.primary.text"
          ),
          onPressAction: () => {
            setEditCommentModalItemIndex(null);
            setOpenRemoveFromBulkPopupId(null);
            removeItemFromList(id);
          },
          loading: false,
          disabled: false,
        },
        {
          type: ButtonType.secondary,
          title: t(
            "bulkInitScreen.removeFromList.confirmPopup.button.secondary.text"
          ),
          onPressAction: () => setOpenRemoveFromBulkPopupId(null),
        },
      ]}
      HtmlSource={{
        html: t("bulkInitScreen.removeFromList.confirmPopup.description.text"),
      }}
      styleKey="Body-R"
      type={messageType.Warning}
    />
  );

  const listContent = (
    <>
      <FlatList
        data={itemList}
        renderItem={renderItem}
        keyExtractor={(item) => item?.id?.toString()}
        contentContainerStyle={contentContainerStyle}
      />
      {openRemoveFromBulkPopupId && (
        <RemoveFromBulkListConfirmPopup id={openRemoveFromBulkPopupId} />
      )}
      <EditBulkItemModal
        item={editModalItem}
        isOpen={editCommentModalItemIndex !== null}
        productType={editModalProductType}
        onClose={changeEditCommentModalIndex}
        removeFromList={_removeItemFromList}
        saveBulkItemDetails={_saveBulkItemDetails}
        requestType={requestType}
        fromRequestModify={fromRequestModify}
        RemoveFromBulkListConfirmPopup={RemoveFromBulkListConfirmPopup}
        openRemoveFromBulkPopupId={openRemoveFromBulkPopupId}
        fromBulkList={fromBulkList}
        stoneType={stoneType}
      />
    </>
  );

  return fromBulkList ? (
    <SelectedItemsProvider>{listContent}</SelectedItemsProvider>
  ) : (
    listContent
  );
};

export default memo(BulkInitSummaryList, (prevProps, nextProps) => {
  return (
    prevProps.itemList === nextProps.itemList &&
    prevProps.removeItemFromList === nextProps.removeItemFromList &&
    prevProps.saveBulkItemDetails === nextProps.saveBulkItemDetails &&
    prevProps.requestType === nextProps.requestType &&
    prevProps.stockItemRequest === nextProps.stockItemRequest &&
    prevProps.hasAnyOfferDetails === nextProps.hasAnyOfferDetails &&
    prevProps.isTotalOfferDiscountApplied ===
      nextProps.isTotalOfferDiscountApplied
  );
});
